import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  PERMISSION_DEPENDENCIES,
  resolvePermissionWithDependencies,
} from "../config/permission-dependencies";
import { PERMISSION_MATRIX } from "../config/permission-matrix";
import styles from "./DependencyInfoPanel.module.scss";

interface DependencyInfoPanelProps {
  open: boolean;
  onClose: () => void;
}

/** Translate permission key → Turkish label by looking it up in PERMISSION_MATRIX. */
function getLabel(key: string): string {
  for (const cat of PERMISSION_MATRIX) {
    const perm = cat.permissions.find((p) => p.key === key);
    if (perm) return `${cat.label} · ${perm.label}`;
  }
  return key;
}

/** Entries shown only if they have at least one dependency. Sort alphabetically. */
function buildEntries(): { key: string; deps: string[]; transitive: string[] }[] {
  return Object.entries(PERMISSION_DEPENDENCIES)
    .filter(([, deps]) => deps.length > 0)
    .map(([key, deps]) => ({
      key,
      deps,
      transitive: resolvePermissionWithDependencies(key).filter(
        (k) => k !== key && !deps.includes(k),
      ),
    }))
    .sort((a, b) => a.key.localeCompare(b.key));
}

const DependencyInfoPanel: React.FC<DependencyInfoPanelProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const entries = useMemo(() => buildEntries(), []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter(
      (e) =>
        e.key.toLowerCase().includes(q) ||
        e.deps.some((d) => d.toLowerCase().includes(q)) ||
        getLabel(e.key).toLowerCase().includes(q),
    );
  }, [entries, search]);

  if (!open) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.panel} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <div>
            <h3>{t("dependencyInfo.title")}</h3>
            <p>
              Bir yetki işaretlendiğinde otomatik olarak işaretlenen bağımlı
              yetkiler. <strong>Uncheck</strong> cascade yapmaz — başka seçili
              bir yetki bağımlılığı hâlâ kullanıyor olabilir.
            </p>
          </div>
          <button
            type="button"
            className={styles.closeBtn}
            aria-label="Kapat"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className={styles.searchWrap}>
          <input
            type="text"
            className={styles.search}
            placeholder={t("dependencyInfo.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className={styles.body}>
          {filtered.length === 0 ? (
            <div className={styles.empty}>{t("dependencyInfo.noMatch")}</div>
          ) : (
            <ul className={styles.list}>
              {filtered.map(({ key, deps, transitive }) => (
                <li key={key} className={styles.row}>
                  <div className={styles.rowHead}>
                    <code>{key}</code>
                    <span className={styles.rowLabel}>{getLabel(key)}</span>
                  </div>
                  <div className={styles.rowBody}>
                    <div className={styles.depGroup}>
                      <span className={styles.depTitle}>{t("dependencyInfo.direct")}</span>
                      <div className={styles.chips}>
                        {deps.map((d) => (
                          <span key={d} className={styles.chip}>
                            <code>{d}</code>
                            <small>{getLabel(d)}</small>
                          </span>
                        ))}
                      </div>
                    </div>
                    {transitive.length > 0 && (
                      <div className={styles.depGroup}>
                        <span className={styles.depTitle}>{t("dependencyInfo.indirect")}</span>
                        <div className={styles.chips}>
                          {transitive.map((d) => (
                            <span
                              key={d}
                              className={`${styles.chip} ${styles.chipMuted}`}
                            >
                              <code>{d}</code>
                              <small>{getLabel(d)}</small>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className={styles.footer}>
          <small>
            Toplam {entries.length} yetki bağımlılık tanımlanmış. Bağımlılığı
            olmayan yetkiler burada gösterilmez.
          </small>
        </div>
      </div>
    </div>
  );
};

export default DependencyInfoPanel;
